import React from 'react';
import ReactMarkdown from 'react-markdown';
import { PopupDialog } from './plugins/components/PopupDialog';
import { remarkPopupPlugin } from './plugins/remarkPopupPlugin';
import { ErrorAlert } from '../ErrorAlert';

interface MarkdownProps {
  children: string;
  popupContent?: React.ReactNode;
  className?: string;
}

interface PopupNode {
  type: 'popup';
  properties: {
    linkText: string;
  };
}

/**
 * Enhanced Markdown component with custom plugins
 *
 * Features:
 * - Standard markdown parsing
 * - Custom [text](popup) tag support
 *
 * Usage:
 * <Markdown popupContent="Your TOS content here">
 *   Welcome! Please read our [Terms of Service](popup) before continuing.
 * </Markdown>
 *
 * The downside of this approach is that we can't use multiple popup links with different
 * content within the same Markdown component.
 *
 * A better approach would be to improve the parser itself to support this, however, at least
 * for a single popup this is good enough, but for other parsers we may would need to something similar
 * to the following:
 * <Markdown config={responseFromBackend} >
 *   Welcome! Please read our [Terms of Service](popup=std_instructions.tos) before continuing.
 *   Additonally this is another [doc](popup=std_instructions.tos_doc)
 * </Markdown>
 * It will show the contents depending upon the popup value.
 */
export const Markdown: React.FC<MarkdownProps> = ({ children, popupContent }) => {
  const components: any = {
    popup: ({ node }: { node: PopupNode }) => {
      if (!popupContent) {
        return (
          <PopupDialog
            dialogContent={
              <ErrorAlert title="Popup content is required" message="Please contact support if you see this message." />
            }
          >
            {node.properties.linkText}
          </PopupDialog>
        );
      }
      return (
        <PopupDialog
          dialogContent={popupContent}
          dialogContentClassName={
            'w-[90vw] max-w-[600px] h-[60vh] bg-widget rounded-2xl shadow-popover overflow-y-auto scrollbar-hide'
          }
        >
          {node.properties.linkText}
        </PopupDialog>
      );
    }
  };

  /*
    Created custom breakpoints to make markdown more responsive depending upon
    the height of the screen. These definitions are in tailwind.config.ts.
  */
  return (
    <div className="responsive-markdown-content-xs h-sm:responsive-markdown-content-sm h-md:responsive-markdown-content-md h-lg:responsive-markdown-content-lg">
      <ReactMarkdown remarkPlugins={[remarkPopupPlugin]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
};
